import React, { useState } from 'react';
import { FileCheck, Download, ListChecks, Loader2 } from 'lucide-react';
import { useVendorTaxReports } from '../../hooks/useVendorTaxReports';
import CustomSelect from '../../components/shared/CustomSelect';
import api from '../../services/authService';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorTaxReports = () => {
 const [year, setYear] = useState(new Date().getFullYear());
 const [downloading, setDownloading] = useState(false);

 const { reports, isLoading, error, formatAmount, generateYearOptions } = useVendorTaxReports(year);

 const cardClass =
 'product-panel p-4 sm:p-6';

 const handleDownloadYear = async () => {
 setDownloading(true);
 try {
 const response = await api.get(`/vendor/tax-reports/download?year=${year}&format=csv`, {
 responseType: 'blob',
 });
 const url = window.URL.createObjectURL(new Blob([response.data]));
 const link = document.createElement('a');
 link.href = url;
 link.setAttribute('download', `tax-report-${year}.csv`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error('Failed to download report:', err);
 alert('Failed to download report. Please try again.');
 } finally {
 setDownloading(false);
 }
 };

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Tax Reports"
 subtitle="Generate and download tax reports for your records."
 actions={
 <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
 <div className="w-full sm:w-48">
 <CustomSelect
 value={year.toString()}
 onChange={(value) => setYear(parseInt(value, 10))}
 options={generateYearOptions().map((y) => ({
 value: y.toString(),
 label: y.toString(),
 }))}
 size="sm"
 className="w-full"
 />
 </div>
 <button
 type="button"
 onClick={handleDownloadYear}
 disabled={downloading}
 className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm disabled:opacity-60"
 >
 {downloading ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <Download className="w-4 h-4" />
 )}
 <span>Download Report</span>
 </button>
 </div>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.taxReports.title}
 steps={VENDOR_PAGE_GUIDES.taxReports.steps}
 icon={ListChecks}
 />

 {isLoading ? (
 <div className="space-y-4">
 {[1, 2].map((i) => (
 <div key={i} className={`${cardClass} animate-pulse`}>
 <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
 <div className="h-4 bg-gray-200 rounded w-1/2" />
 </div>
 ))}
 </div>
 ) : error ? (
 <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
 Failed to load tax reports. Please try again.
 </div>
 ) : (
 <>
 <div className={cardClass}>
 <h2 className="text-lg font-semibold text-ink mb-4">
 Tax Summary for {year}
 </h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
 <div className="rounded-xl border border-border bg-surface-muted p-3">
 <p className="text-sm text-ink-secondary mb-1">Total Revenue</p>
 <p className="text-xl font-bold text-ink tabular-nums">
 ${formatAmount(reports.total_revenue)}
 </p>
 </div>
 <div className="rounded-xl border border-border bg-surface-muted p-3">
 <p className="text-sm text-ink-secondary mb-1">Total Expenses</p>
 <p className="text-xl font-bold text-ink tabular-nums">
 ${formatAmount(reports.total_expenses)}
 </p>
 </div>
 <div className="rounded-xl border border-border bg-surface-muted p-3">
 <p className="text-sm text-ink-secondary mb-1">Net Income</p>
 <p className="text-xl font-bold text-emerald-600 tabular-nums">
 ${formatAmount(reports.net_income)}
 </p>
 </div>
 <div className="rounded-xl border border-border bg-surface-muted p-3">
 <p className="text-sm text-ink-secondary mb-1">Tax Owed</p>
 <p className="text-xl font-bold text-red-600 tabular-nums">
 ${formatAmount(reports.tax_owed)}
 </p>
 </div>
 </div>
 </div>

 <div className={cardClass}>
 <h2 className="text-lg font-semibold text-ink mb-4">
 Available Reports
 </h2>
 {reports.quarterly_reports && reports.quarterly_reports.length > 0 ? (
 <div className="space-y-3">
 {reports.quarterly_reports.map((report, index) => (
 <div
 key={index}
 className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-border bg-surface-muted p-4"
 >
 <div className="flex items-center gap-3 min-w-0">
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-gray-400">
 <FileCheck className="w-4 h-4" />
 </div>
 <div>
 <p className="font-medium text-ink">{report.quarter}</p>
 <p className="text-sm text-ink-tertiary">
 Revenue: ${formatAmount(report.revenue)}
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={() => {
 const quarter = report.quarter.replace(` ${year}`, '').toLowerCase();
 const url = `/vendor/tax-reports/download?year=${year}&quarter=${quarter}&format=csv`;
 window.open(url, '_blank');
 }}
 className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto"
 >
 <Download className="w-4 h-4" />
 <span>Download</span>
 </button>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-10">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <FileCheck className="w-7 h-7" />
 </div>
 <p className="text-ink-secondary">No tax reports available for {year}</p>
 <p className="text-sm text-ink-tertiary mt-2">
 Generate a report to get started
 </p>
 </div>
 )}
 </div>
 </>
 )}
 </div>
 );
};

export default VendorTaxReports;
